import { useBlockedIntervals } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/BlockedIntervalsContext";
import { useCalendarAppointments } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarAppointmentsContext";
import { useCalendarGroupSessions } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarGroupSessionsContext";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import { useSessionTypes } from "contexts/SessionTypesContext";
import messages from "messages";
import moment from "moment-timezone";
import { useMemo } from "react";
import {
  getAppointmentSessionTypeColor,
  getAppointmentTitle,
  getSessionIndicatorIconsDataForAppointment,
  isAppointmentCancelled,
} from "utils/calendar-event-utils/appointment-utils";
import { isGroupSessionCancelledByPro } from "utils/isGroupSessionCancelled";
import { DayPreviewEvent } from "../day-preview.types";

const generateTemporaryId = () => Math.random().toString().substring(2);

const useEventsForDay = (dayStartValue: number) => {
  const { currentCalendarTimezone } = useCalendarTime();

  const { sessionTypes } = useSessionTypes();

  const { groupSessions } = useCalendarGroupSessions();
  const { appointments } = useCalendarAppointments();
  const { gcalEvents, blockedIntervals } = useBlockedIntervals();

  const appointmentsForDay: DayPreviewEvent[] = useMemo(
    () =>
      appointments
        .filter(
          (appointment) =>
            moment
              .tz(appointment.appointment_start, currentCalendarTimezone)
              .startOf("day")
              .valueOf() === dayStartValue,
        )
        .map((appointment) => ({
          type: "appointment",
          item: appointment,
          startISO: appointment.appointment_start,
          endISO: appointment.appointment_end,
          id: appointment.appointment_id.toString(),
          title: getAppointmentTitle(appointment),
          subtitle: appointment.appointment_type_label,
          isCancelled: isAppointmentCancelled(appointment),
          sessionTypeColor: getAppointmentSessionTypeColor(
            appointment,
            sessionTypes,
          ),
          sessionIndicatorIconsData:
            getSessionIndicatorIconsDataForAppointment(appointment),
        })),
    [appointments, currentCalendarTimezone, dayStartValue, sessionTypes],
  );

  const groupSessionsForDay: DayPreviewEvent[] = useMemo(
    () =>
      groupSessions
        .filter(
          (groupSession) =>
            moment
              .tz(groupSession.group_session_start, currentCalendarTimezone)
              .startOf("day")
              .valueOf() === dayStartValue,
        )
        .map((groupSession) => ({
          type: "groupSession",
          item: groupSession,
          startISO: groupSession.group_session_start,
          endISO: groupSession.group_session_end,
          id: groupSession.group_session_id.toString(),
          title: groupSession.topic || `(${messages.noTitle})`,
          isCancelled: isGroupSessionCancelledByPro(groupSession),
        })),
    [groupSessions, currentCalendarTimezone, dayStartValue],
  );

  const gcalEventsForDay: DayPreviewEvent[] = useMemo(
    () =>
      gcalEvents
        .filter(
          (gcalEvent) =>
            moment
              .tz(gcalEvent.start, currentCalendarTimezone)
              .startOf("day")
              .valueOf() === dayStartValue,
        )
        .map((gcalEvent) => ({
          type: "gcalEvent",
          item: gcalEvent,
          startISO: gcalEvent.start,
          endISO: gcalEvent.end,
          // TODO: This is a temporary fix for the fact that we don't have a timeslot_id for gcal events
          id: gcalEvent.timeslot_id?.toString() ?? generateTemporaryId(),
          title: gcalEvent.busy_note || `(${messages.noTitle})`,
          subtitle: gcalEvent.calendar_name || "",
        })),
    [gcalEvents, currentCalendarTimezone, dayStartValue],
  );

  const blockedIntervalsForDay: DayPreviewEvent[] = useMemo(
    () =>
      blockedIntervals
        .filter(
          (blockedInterval) =>
            moment
              .tz(blockedInterval.start, currentCalendarTimezone)
              .startOf("day")
              .valueOf() === dayStartValue,
        )
        .map((blockedInterval) => ({
          type: "blockedInterval",
          item: blockedInterval,
          startISO: blockedInterval.start,
          endISO: blockedInterval.end,
          // TODO: This is a temporary fix for the fact that timeslot_id is not required for blocked intervals
          id: blockedInterval.timeslot_id?.toString() ?? generateTemporaryId(),
          title: blockedInterval.busy_note || `(${messages.noTitle})`,
        })),
    [blockedIntervals, currentCalendarTimezone, dayStartValue],
  );

  const eventsForDay = useMemo(
    () =>
      [
        ...appointmentsForDay,
        ...groupSessionsForDay,
        ...gcalEventsForDay,
        ...blockedIntervalsForDay,
      ].sort((a, b) => {
        if (a.startISO < b.startISO) {
          return -1;
        }
        if (a.startISO > b.startISO) {
          return 1;
        }
        return 0;
      }),
    [
      appointmentsForDay,
      groupSessionsForDay,
      gcalEventsForDay,
      blockedIntervalsForDay,
    ],
  );

  return {
    eventsForDay,
  };
};

export default useEventsForDay;
